/*
 * Carbisdale - DictationSocket
 *
 * WebSocket-Verbindung zu Deepgram fuer Echtzeit-Transkription.
 * Sendet PCM-Audio, empfaengt interim/final Transkripte.
 */

import { DICTATION } from '../config.js'

/**
 * Deepgram WebSocket-Event-Typen
 */
const DeepgramEventType = Object.freeze({
    RESULTS: 'Results',
    UTTERANCE_END: 'UtteranceEnd',
    METADATA: 'Metadata',
    ERROR: 'Error'
})

export class DictationSocket {
    constructor() {
        this._ws = null

        /** @type {function(string, boolean): void|null} Callback: (text, isFinal) */
        this.onTranscript = null

        /** @type {function(): void|null} Callback: laengere Stille erkannt */
        this.onUtteranceEnd = null

        /** @type {function(Error): void|null} Callback: Fehler */
        this.onError = null

        /** @type {function(): void|null} Callback: Verbindung offen */
        this.onOpen = null

        /** @type {function(): void|null} Callback: Verbindung geschlossen */
        this.onClose = null
    }

    /**
     * Oeffnet WebSocket-Verbindung zu Deepgram.
     * @param {string} token - Temporaeres Deepgram-Token
     */
    connect(token) {
        const params = new URLSearchParams({
            model: DICTATION.model,
            language: DICTATION.language,
            smart_format: 'true',
            interim_results: 'true',
            endpointing: String(DICTATION.endpointingMs),
            encoding: 'linear16',
            sample_rate: String(DICTATION.sampleRate),
            channels: '1'
        })
        // UtteranceEnd nur aktivieren wenn konfiguriert (0 = deaktiviert)
        if (DICTATION.utteranceEndMs > 0) {
            params.set('utterance_end_ms', String(DICTATION.utteranceEndMs))
        }

        const url = `wss://api.deepgram.com/v1/listen?${params}`
        // Grant-API liefert JWTs → 'bearer', API-Keys → 'token'
        this._ws = new WebSocket(url, ['bearer', token])
        this._ws.binaryType = 'arraybuffer'

        this._ws.onopen = () => {
            if (this.onOpen) this.onOpen()
        }

        this._ws.onmessage = (event) => {
            this._handleMessage(event.data)
        }

        this._ws.onerror = () => {
            if (this.onError) this.onError(new Error('WebSocket error'))
        }

        this._ws.onclose = () => {
            if (this.onClose) this.onClose()
        }
    }

    /**
     * Sendet einen Audio-Chunk an Deepgram.
     * @param {ArrayBuffer} audioData - PCM Int16 Daten
     */
    send(audioData) {
        if (this._ws && this._ws.readyState === WebSocket.OPEN) {
            this._ws.send(audioData)
        }
    }

    /**
     * Schliesst die Verbindung sauber.
     * Sendet CloseStream-Message, damit Deepgram letzte Finals liefert.
     */
    close() {
        if (this._ws) {
            const ws = this._ws
            if (ws.readyState === WebSocket.OPEN) {
                // CloseStream-Message: Deepgram liefert verbleibende Finals
                ws.send(JSON.stringify({ type: 'CloseStream' }))
            }
            // WebSocket nach kurzem Delay schliessen (Finals abwarten).
            // Referenz gespeichert, damit bei schnellem Reconnect nicht
            // versehentlich ein neues WebSocket geschlossen wird.
            setTimeout(() => {
                if (ws.readyState !== WebSocket.CLOSED) ws.close()
                if (this._ws === ws) this._ws = null
            }, 500)
        }
    }

    /**
     * Sofortiges Schliessen ohne auf Finals zu warten.
     */
    destroy() {
        if (this._ws) {
            this._ws.onmessage = null
            this._ws.onerror = null
            this._ws.onclose = null
            this._ws.close()
            this._ws = null
        }
    }

    /**
     * @returns {boolean} true wenn WebSocket offen ist
     */
    get isConnected() {
        return this._ws !== null && this._ws.readyState === WebSocket.OPEN
    }

    /**
     * Verarbeitet eingehende Deepgram-Messages.
     * @param {string} data - JSON-String
     */
    _handleMessage(data) {
        // Binaere Daten ignorieren (sollte nicht vorkommen)
        if (typeof data !== 'string') return

        let msg
        try {
            msg = JSON.parse(data)
        } catch {
            return
        }

        switch (msg.type) {
            case DeepgramEventType.RESULTS:
                this._handleResults(msg)
                break
            case DeepgramEventType.UTTERANCE_END:
                if (this.onUtteranceEnd) this.onUtteranceEnd()
                break
            case DeepgramEventType.ERROR:
                if (this.onError) this.onError(new Error(msg.description || 'Deepgram error'))
                break
        }
    }

    /**
     * Verarbeitet Transkript-Ergebnisse.
     * @param {object} msg - Deepgram Results-Message
     */
    _handleResults(msg) {
        const alt = msg.channel?.alternatives?.[0]
        if (!alt || !alt.transcript) return

        const text = alt.transcript.trim()
        if (text === '') return

        const isFinal = msg.is_final === true
        if (this.onTranscript) {
            this.onTranscript(text, isFinal)
        }
    }
}
